import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import Loading from "../../../../common-components/Loading";
import {
  getIndirectVersionsAPI,
  getIndirectSummaryVersionComparisonFringeAPI,
  downloadSummaryVsPriorFringeAPI,
} from "../../../../api/reports-api";

import {
  TABLE_FONT,
  TH_BG,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TOTAL_BG,
  TOTAL_BG_ALT,
  ACCENT_COLOR,
  TABLE_BORDER,
  CELL_FG_MUTED,
  CELL_FG_STRONG,
  TH_BG_BAND,
  TH_TEXT,
  TABLE_SCROLL_WRAP_STYLE,
} from "../../reportTableConfig";
import { PL_POS_C, PL_NEG_C } from "../pl-by-program/plByProgramTheme";

const HEADER_BG = TH_BG;
const HEADER_COL = TH_BG;

/* ── Formatter ── */
function fmtI(v) {
  const n = (v === null || v === undefined || isNaN(Number(v))) ? 0 : Number(v);
  const abs = Math.abs(n);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${s})` : `$${s}`;
}

/* Normalize a row → internal {label, mtdA, mtdB, ytdA, ytdB, fyA, fyB} shape */
function normalizeRow(r) {
  const pick = (...keys) => {
    for (const k of keys) {
      if (r[k] != null) return r[k];
    }
    return 0;
  };
  return {
    label:
      r.label ??
      r.type ??
      r.costType ??
      r.cost_type ??
      r.accountId ??
      r.account_id ??
      r.accountName ??
      "",
    mtdA: pick("mtdActuals", "mtdFrom", "mtdA", "mtd_actuals", "mtd_from"),
    mtdB: pick(
      "mtdBudget",
      "mtdTo",
      "mtdPrior",
      "mtdB",
      "mtd_budget",
      "mtd_to",
    ),
    ytdA: pick("ytdActuals", "ytdFrom", "ytdA", "ytd_actuals", "ytd_from"),
    ytdB: pick(
      "ytdBudget",
      "ytdTo",
      "ytdPrior",
      "ytdB",
      "ytd_budget",
      "ytd_to",
    ),
    fyA: pick("fyFcst", "fyFrom", "fyActuals", "fyA", "fy_fcst", "fy_from"),
    fyB: pick("fyBudget", "fyTo", "fyPrior", "fyB", "fy_budget", "fy_to"),
  };
}

function computeTotal(rows) {
  return rows.reduce(
    (acc, r) => ({
      mtdA: acc.mtdA + r.mtdA,
      mtdB: acc.mtdB + r.mtdB,
      ytdA: acc.ytdA + r.ytdA,
      ytdB: acc.ytdB + r.ytdB,
      fyA: acc.fyA + r.fyA,
      fyB: acc.fyB + r.fyB,
    }),
    { mtdA: 0, mtdB: 0, ytdA: 0, ytdB: 0, fyA: 0, fyB: 0 },
  );
}

function extractTable(raw) {
  const arr = Array.isArray(raw) ? raw : [];
  const getLabel = (r) =>
    (r.label ?? r.type ?? r.costType ?? r.cost_type ?? "")
      .toString()
      .toLowerCase()
      .trim();
  const rows = arr
    .filter((r) => getLabel(r) !== "total")
    .map(normalizeRow);
  const total = computeTotal(rows);
  return { rows, total };
}

const EMPTY_TOTAL = { mtdA: 0, mtdB: 0, ytdA: 0, ytdB: 0, fyA: 0, fyB: 0 };

/* ── Styles ── */
const useStyles = makeStyles(() => ({
  svbFilters: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  svbFilterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    flexShrink: 0,
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
    "&:focus-within": {
      borderColor: "#A5B4FC",
      boxShadow: "0 0 0 3px rgba(240,242,255,0.9)",
    },
  },
  svbFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: "'IBM Plex Sans', sans-serif",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  svbFilterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 110,
    height: "100%",
    fontFamily: "'IBM Plex Sans', sans-serif",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },
}));

const FONT_SVP = "'IBM Plex Sans', sans-serif";

const TOTAL_FG = CELL_FG_STRONG;

const TYPE_W_SVP = 200;
const COL_W_SVP = 110;
const Z_H = 6,
  Z_S = 4,
  GH_SVP = 36,
  CH_SVP = 38,
  RH_SVP = 44;
const TH_SH_G_SVP = "inset 0 -1px 0 #4a4a4c";
const TH_SH_SVP = "inset 0 -1px 0 #4a4a4c";
const TOTAL_TOP_SVP = GH_SVP + CH_SVP - 1;

const thCoreSVP = (top, h, z, pad, bg) => ({
  position: "sticky",
  top,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: bg,
  color: TH_TEXT,
  verticalAlign: "middle",
  padding: pad,
  whiteSpace: "nowrap",
  borderRight: "1px solid #4a4a4c",
  borderBottom: "none",
});
const thBandSVP = () => ({
  ...thCoreSVP(0, GH_SVP, Z_H, "7px 14px", HEADER_BG),
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textAlign: "center",
  boxShadow: TH_SH_G_SVP,
});
const thColSVP = () => ({
  ...thCoreSVP(GH_SVP, CH_SVP, Z_H, "6px 10px", HEADER_COL),
  fontSize: 10,
  fontWeight: 600,
  textAlign: "right",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  minWidth: COL_W_SVP,
  boxShadow: TH_SH_SVP,
});
const tdBaseSVP = (bg, fw = 400, isFirst = false) => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: "right",
  fontWeight: fw,
  padding: "7px 10px",
  fontSize: 12,
  color: CELL_FG,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
  ...(isFirst ? { borderLeft: "2px solid #C8D0DC" } : {}),
});
const tdTypeSVP = (bg) => ({
  ...tdBaseSVP(bg, 500),
  position: "sticky",
  left: 0,
  zIndex: Z_S,
  width: TYPE_W_SVP,
  minWidth: TYPE_W_SVP,
  maxWidth: TYPE_W_SVP,
  textAlign: "left",
  padding: "7px 14px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
});
const tdTotalTypeSVP = () => ({
  ...tdTypeSVP(TOTAL_BG),
  fontWeight: 700,
  color: TOTAL_FG,
  position: "sticky",
  top: TOTAL_TOP_SVP,
  zIndex: Z_S + 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.08)",
});
const tdTotalValSVP = (isFirst = false) => ({
  ...tdBaseSVP(TOTAL_BG, 700, isFirst),
  color: TOTAL_FG,
  position: "sticky",
  top: TOTAL_TOP_SVP,
  zIndex: 2,
  borderBottom: "2px solid #CBD5E1",
  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
});

const CARD_SVP = {
  background: "#FFFFFF",
  borderRadius: 16,
  border: "1px solid rgba(0,0,0,0.08)",
  overflow: "hidden",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
};
const CHEAD_SVP = {
  padding: "18px 28px",
  borderBottom: "2px solid #F0F2F5",
  background: "#FFFFFF",
};
const CTITLE_SVP = {
  fontSize: 17,
  fontWeight: 800,
  color: "#111827",
  letterSpacing: "-0.02em",
  fontFamily: FONT_SVP,
  margin: 0,
};
const CMETA_SVP = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT_SVP,
  marginTop: 2,
};

function SVPTable({
  title,
  subtitle,
  bandLabels,
  typeLabel,
  rows,
  total,
  fromLabel = "From",
  toLabel = "To",
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) => setSort((s) =>
    s.key === key ? (s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }) : { key, dir: "asc" }
  );

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const TABLE_W = TYPE_W_SVP + 9 * COL_W_SVP;
  const [b1, b2, b3] = bandLabels;
  const vColor = (v) => (v === 0 || v == null ? CELL_FG : v > 0 ? PL_POS_C : PL_NEG_C);
  const sortedRows = sort.key === null ? rows : [...rows].sort((a, b) => {
    const mult = sort.dir === "asc" ? 1 : -1;
    if (sort.key === "label") return a.label.localeCompare(b.label) * mult;
    if (sort.key === "mtdV") return ((a.mtdA - a.mtdB) - (b.mtdA - b.mtdB)) * mult;
    if (sort.key === "ytdV") return ((a.ytdA - a.ytdB) - (b.ytdA - b.ytdB)) * mult;
    if (sort.key === "fyV")  return ((a.fyA  - a.fyB)  - (b.fyA  - b.fyB))  * mult;
    return ((a[sort.key] ?? 0) - (b[sort.key] ?? 0)) * mult;
  });
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const ps = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...ps]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();
  const pgBtn = (active) => ({
    minWidth: 30,
    height: 28,
    padding: "0 6px",
    border: `1px solid ${active ? TH_BG_BAND : "#E2E8F0"}`,
    borderRadius: 6,
    background: active ? TH_BG_BAND : "#fff",
    fontSize: 12,
    color: active ? "#fff" : TH_BG_BAND,
    cursor: "pointer",
    fontFamily: FONT_SVP,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    fontWeight: active ? 600 : 400,
  });

  return (
    <div style={CARD_SVP}>
      <div style={CHEAD_SVP}>
        <h2 style={CTITLE_SVP}>{title || typeLabel}</h2>
        {subtitle && <div style={CMETA_SVP}>{subtitle}</div>}
      </div>
      <div style={TABLE_SCROLL_WRAP_STYLE}>
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            fontFamily: FONT_SVP,
            fontSize: 12,
            width: "100%",
            minWidth: TABLE_W,
          }}
        >
          <colgroup>
            <col style={{ width: TYPE_W_SVP }} />
            {Array.from({ length: 9 }, (_, i) => (
              <col key={i} style={{ width: COL_W_SVP }} />
            ))}
          </colgroup>
          <thead style={{ background: TH_BG }}>
            <tr style={{ height: GH_SVP }}>
              <th
                style={{
                  ...thCoreSVP(
                    0,
                    GH_SVP + CH_SVP,
                    Z_H + 1,
                    "7px 14px",
                    HEADER_BG,
                  ),
                  left: 0,
                  width: TYPE_W_SVP,
                  minWidth: TYPE_W_SVP,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  textAlign: "left",
                  boxShadow: `4px 0 8px -2px rgba(0,0,0,0.12), ${TH_SH_SVP}`,
                }}
                rowSpan={2}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                  <span>{typeLabel}</span>
                  <button onClick={() => toggleSort("label")}
                    title={sort.key === "label" ? (sort.dir === "asc" ? "Sorted A→Z (click for Z→A)" : "Sorted Z→A (click to clear)") : "Sort by Type"}
                    style={{ flexShrink: 0, width: 18, height: 16, border: "none", background: "transparent", cursor: "pointer", padding: 0, borderRadius: 3, color: sort.key === "label" ? "#A5B4FC" : "#fff", opacity: sort.key === "label" ? 1 : 0.45, fontSize: 11, display: "inline-flex", alignItems: "center", justifyContent: "center", textTransform: "none" }}>
                    {sort.key === "label" ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                  </button>
                </div>
              </th>
              <th colSpan={3} style={thBandSVP()}>
                {b1}
              </th>
              <th colSpan={3} style={thBandSVP()}>
                {b2}
              </th>
              <th colSpan={3} style={thBandSVP()}>
                {b3}
              </th>
            </tr>
            <tr style={{ height: CH_SVP }}>
              {[["mtdA","mtdB","mtdV"],["ytdA","ytdB","ytdV"],["fyA","fyB","fyV"]].map(([kA, kB, kV]) => (
                <React.Fragment key={kA}>
                  {[[kA, fromLabel],[kB, toLabel],[kV, "Variance"]].map(([k, lbl]) => (
                    <th key={k} title={sort.key === k ? (sort.dir === "asc" ? "Sorted ascending" : "Sorted descending") : "Sort"} style={{ ...thColSVP(), cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort(k)}>
                      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 3, width: "100%" }}>
                        {lbl}
                        <span style={{ fontSize: 9, color: sort.key === k ? "#A5B4FC" : "rgba(255,255,255,0.4)" }}>
                          {sort.key === k ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                        </span>
                      </span>
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ height: RH_SVP }}>
              <td style={tdTotalTypeSVP()}>Total</td>
              {[
                { a: total.mtdA, b: total.mtdB },
                { a: total.ytdA, b: total.ytdB },
                { a: total.fyA, b: total.fyB },
              ].flatMap(({ a, b }, gi) => {
                const v = a - b;
                return [
                  <td key={gi + "a"} style={tdTotalValSVP(true)}>
                    {fmtI(a)}
                  </td>,
                  <td key={gi + "b"} style={tdTotalValSVP()}>
                    {fmtI(b)}
                  </td>,
                  <td
                    key={gi + "v"}
                    style={{ ...tdTotalValSVP(), color: vColor(v) }}
                  >
                    {fmtI(v)}
                  </td>,
                ];
              })}
            </tr>
            {pagedRows.map((row, i) => {
              const bg =
                ((safePage - 1) * pageSize + i) % 2 === 0 ? EVEN_BG : ODD_BG;
              const vM = row.mtdA - row.mtdB,
                vY = row.ytdA - row.ytdB,
                vF = row.fyA - row.fyB;
              return (
                <tr key={i} style={{ height: RH_SVP }}>
                  <td style={tdTypeSVP(bg)} title={row.label}>{row.label}</td>
                  <td style={tdBaseSVP(bg, 400, true)}>{fmtI(row.mtdA)}</td>
                  <td style={tdBaseSVP(bg)}>{fmtI(row.mtdB)}</td>
                  <td style={{ ...tdBaseSVP(bg), color: vColor(vM) }}>
                    {fmtI(vM)}
                  </td>
                  <td style={tdBaseSVP(bg, 400, true)}>{fmtI(row.ytdA)}</td>
                  <td style={tdBaseSVP(bg)}>{fmtI(row.ytdB)}</td>
                  <td style={{ ...tdBaseSVP(bg), color: vColor(vY) }}>
                    {fmtI(vY)}
                  </td>
                  <td style={tdBaseSVP(bg, 400, true)}>{fmtI(row.fyA)}</td>
                  <td style={tdBaseSVP(bg)}>{fmtI(row.fyB)}</td>
                  <td style={{ ...tdBaseSVP(bg), color: vColor(vF) }}>
                    {fmtI(vF)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Pagination bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 20px",
          borderTop: "1px solid #E2E8F0",
          background: "#FAFAFA",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 12,
            color: "#94A3B8",
            fontFamily: FONT_SVP,
            whiteSpace: "nowrap",
          }}
        >
          {rows.length} row{rows.length !== 1 ? "s" : ""}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontSize: 12,
              color: "#94A3B8",
              fontFamily: FONT_SVP,
              whiteSpace: "nowrap",
            }}
          >
            Rows Per Page
          </span>
          <select
            style={{
              height: 28,
              border: "1px solid #E2E8F0",
              borderRadius: 6,
              fontSize: 12,
              color: "#374151",
              padding: "0 6px",
              background: "#fff",
              cursor: "pointer",
              outline: "none",
              fontFamily: FONT_SVP,
            }}
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100, ...(rows.length > 100 ? [rows.length] : [])].map(
              (n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ),
            )}
          </select>
          {totalPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <button
                style={pgBtn(false)}
                disabled={safePage === 1}
                onClick={() => setPage(1)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="7,2 3,6 7,10" />
                  <polyline points="11,2 7,6 11,10" />
                </svg>
              </button>
              <button
                style={pgBtn(false)}
                disabled={safePage === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="8,2 4,6 8,10" />
                </svg>
              </button>
              {pageNumbers.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && pageNumbers[i - 1] < p - 1 && (
                    <span
                      style={{
                        fontSize: 12,
                        color: "#94A3B8",
                        padding: "0 4px",
                        userSelect: "none",
                      }}
                    >
                      …
                    </span>
                  )}
                  <button
                    style={pgBtn(p === safePage)}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
              <button
                style={pgBtn(false)}
                disabled={safePage === totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="4,2 8,6 4,10" />
                </svg>
              </button>
              <button
                style={pgBtn(false)}
                disabled={safePage === totalPages}
                onClick={() => setPage(totalPages)}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="5,2 9,6 5,10" />
                  <polyline points="1,2 5,6 1,10" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default function SummaryVsPriorFringe({
  filters = {},
  actualsMonth,
  onActualsMonthChange,
  onHeaderMonthChange,
  downloadRef,
  setParentDownloading,
}) {
  const classes = useStyles();
  const [metricType, setMetricType] = useState("Cost Type");
  const [fromEAC, setFromEAC] = useState("");
  const [toEAC, setToEAC] = useState("");
  const [versions, setVersions] = useState([]);

  const [typeRows, setTypeRows] = useState([]);
  const [typeTotal, setTypeTotal] = useState(EMPTY_TOTAL);
  const [metricRows, setMetricRows] = useState([]);
  const [metricTotal, setMetricTotal] = useState(EMPTY_TOTAL);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    setParentDownloading?.(true);
    try {
      const f = { monthName: [actualsMonth], metricSelection: metricType };
      if (fromEAC) f.version = fromEAC;
      if (toEAC) f.toVersion = toEAC;
      if (filters.year) f.financialYear = String(filters.year);
      if (filters.monthName?.length)
        f.month = filters.monthName.map(
          (m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase(),
        );
      else if (filters.quarter?.length) f.quarter = filters.quarter;
      if (filters.costTypeFringe?.length) f.costTypeFringe = filters.costTypeFringe;
      if (filters.account?.length) f.acctIdndname = filters.account;
      const { blob, filename } = await downloadSummaryVsPriorFringeAPI(f);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      /* silent */
    } finally {
      setParentDownloading?.(false);
    }
  };
  if (downloadRef) downloadRef.current = handleDownload;

  /* Fetch versions once */
  useEffect(() => {
    getIndirectVersionsAPI().then((res) => {
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : [];
      const normalized = list
        .map((v) => {
          if (typeof v === "object" && v !== null) {
            const id =
              v.key ?? v.id ?? v.version ?? v.versionId ?? v.version_id ?? "";
            const label =
              v.name ?? v.version_name ?? v.versionName ?? String(id);
            return { id, label };
          }
          return { id: String(v), label: String(v) };
        })
        .filter((v) => v.id);
      setVersions(normalized);
      if (normalized.length > 0) {
        setFromEAC(
          (prev) => prev || normalized[0].id,
        );
        setToEAC(
          (prev) => prev || (normalized[1] ?? normalized[0]).id,
        );
      }
    });
  }, []);

  /* Fetch table data */
  useEffect(() => {
    if (actualsMonth == null || !fromEAC || !toEAC) return;

    setLoading(true);
    setError(null);

    const f = { monthName: [actualsMonth], metricSelection: metricType };
    if (fromEAC) f.version = fromEAC;
    if (toEAC) f.toVersion = toEAC;
    if (filters.year) f.financialYear = String(filters.year);
    if (filters.costTypeFringe) f.costTypeFringe = filters.costTypeFringe;
    if (filters.account?.length) f.acctIdndname = filters.account;
    if (filters.monthName?.length)
      f.month = filters.monthName.map(
        (m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase(),
      );
    else if (filters.quarter?.length) f.quarter = filters.quarter;

    getIndirectSummaryVersionComparisonFringeAPI(f)
      .then((res) => {
        const top = res?.data ?? res ?? {};

        const tbl = extractTable(top.typeTable);
        const mtbl = extractTable(top.metricTable);

        setTypeRows(tbl.rows);
        setMetricRows(mtbl.rows);
        setMetricTotal(mtbl.total);
        setTypeTotal(tbl.rows.length > 0 ? tbl.total : mtbl.total);

        setLoading(false);

        const apiActualsMonth = top.actualsMonth;
        onHeaderMonthChange?.(
          Array.isArray(apiActualsMonth) && apiActualsMonth.length
            ? apiActualsMonth[apiActualsMonth.length - 1]
            : null,
        );
      })
      .catch(() => {
        setError("Failed to load data.");
        setLoading(false);
      });
  }, [actualsMonth, metricType, fromEAC, toEAC, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const _hasUnsupported =
    filters.department?.length || filters.subDepartment?.length;
  const _hasWorking = filters.quarter?.length || filters.monthName?.length;
  if (_hasUnsupported && !_hasWorking) {
    return (
      <div style={{ padding: 32 }}>
        <div
          style={{
            background: "#fff",
            borderRadius: 8,
            boxShadow: "0 1px 4px rgba(0,0,0,0.10)",
            padding: "48px 24px",
            textAlign: "center",
            color: "#94A3B8",
            fontSize: 14,
            fontFamily: FONT_SVP,
          }}
        >
          No Data
        </div>
      </div>
    );
  }

  const fromLabel = versions.find((v) => v.id === fromEAC)?.label ?? fromEAC;
  const toLabel = versions.find((v) => v.id === toEAC)?.label ?? toEAC;
  const fyYear = filters.year || new Date().getFullYear();
  const bandLabels = [
    `MTD ${actualsMonth}-${fyYear}`,
    `YTD ${actualsMonth}-${fyYear}`,
    `${actualsMonth} Actuals + Fcst FY-${fyYear}`,
  ];

  return (
    <div>
      <div className={classes.svbFilters}>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>Actuals Selection</span>
          <select
            className={classes.svbFilterSelect}
            value={actualsMonth ?? ""}
            onChange={(e) => onActualsMonthChange?.(e.target.value)}
          >
            {MONTHS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>Metric Selection</span>
          <select
            className={classes.svbFilterSelect}
            value={metricType}
            onChange={(e) => setMetricType(e.target.value)}
          >
            <option>Cost Type</option>
            <option>Account ID &amp; Name</option>
          </select>
        </div>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>From</span>
          <select
            className={classes.svbFilterSelect}
            value={fromEAC}
            onChange={(e) => setFromEAC(e.target.value)}
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
        <div className={classes.svbFilterGroup}>
          <span className={classes.svbFilterLabel}>To</span>
          <select
            className={classes.svbFilterSelect}
            value={toEAC}
            onChange={(e) => setToEAC(e.target.value)}
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading || actualsMonth == null || !fromEAC || !toEAC ? (
        <Loading />
      ) : error ? (
        <div
          style={{
            padding: "8px 0 12px",
            fontFamily: FONT_SVP,
            fontSize: 13,
            color: "#DC2626",
          }}
        >
          {error}
        </div>
      ) : (
        <>
          <SVPTable
            title="Type"
            subtitle="Fringe — Version Comparison"
            bandLabels={bandLabels}
            typeLabel="Type"
            rows={typeRows}
            total={typeTotal}
            fromLabel={fromLabel}
            toLabel={toLabel}
          />
          {metricRows.length > 0 && (
            <SVPTable
              title={metricType}
              subtitle="Fringe — Version Comparison"
              bandLabels={bandLabels}
              typeLabel={metricType}
              rows={metricRows}
              total={metricTotal}
              fromLabel={fromLabel}
              toLabel={toLabel}
            />
          )}
        </>
      )}
    </div>
  );
}
